import { useHttp } from '@/shared/lib'
import { computed, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import {
  filterSubTypesForCatalog,
  parseCatalogTypeMeta,
  type ObjectSubTypeOption,
} from './helpers'

/**
 * Загружает метаданные catalog_invent_objects_type для резолва object_type_id.
 */
export const useLoadCatalogTypeMeta = (objectId: Ref<string>, typeRefId: Ref<number>) => {
  const http = useHttp()

  return useQuery({
    queryKey: computed(() => ['catalog_type_meta', objectId.value, typeRefId.value]),
    queryFn: async () => {
      const res = await http.get(
        `/inventory/group/${objectId.value}/object-types/${typeRefId.value}`,
      )
      return parseCatalogTypeMeta(res, typeRefId.value)
    },
    retry: false,
    enabled: () => Boolean(objectId.value) && Boolean(typeRefId.value),
  })
}

/**
 * Загружает subtypes для list_types: резолвит catalog_id → object_type_id,
 * для корневых справочников фильтрует по parent_invent_type_id.
 */
export const useLoadSubTypesList = (objectId: Ref<string>, objectTypeId: Ref<number>) => {
  const http = useHttp()
  const metaQuery = useLoadCatalogTypeMeta(objectId, objectTypeId)

  const queryKey = computed(() => [
    'load_sub_type_list',
    objectId.value,
    objectTypeId.value,
    metaQuery.data.value?.objectTypeId,
    metaQuery.data.value?.catalogRowId,
    metaQuery.data.value?.isDictionaryRoot,
  ])

  return useQuery({
    queryKey,
    queryFn: async () => {
      const meta = metaQuery.data.value!
      const res = await http.get(
        `/inventory/objects/${objectId.value}/object-types/${meta.objectTypeId}/subtypes`,
      )
      const rawSubTypes = res?.data?.sub_types
      if (!Array.isArray(rawSubTypes)) return res

      const filtered = filterSubTypesForCatalog(rawSubTypes as ObjectSubTypeOption[], meta)
      return {
        ...res,
        data: {
          ...(res.data ?? {}),
          sub_types: filtered,
        },
      }
    },
    retry: false,
    enabled: () =>
      Boolean(objectId.value) &&
      Boolean(objectTypeId.value) &&
      Boolean(metaQuery.data.value?.objectTypeId),
  })
}
